import { useMemo } from 'react';
import { useTranslation } from '@sib/i18n';
import { UploadQueueGuardDialogProps } from '../types/upload-queue-guard';
import { ConfirmationDialog } from './ConfirmationDialog';

export const UploadQueueGuardDialog = ({
  open,
  displayTotalFilesCount,
  errorFilesCount,
  uploadingFilesCount,
  onClose,
  onConfirm,
  onOpenFileManager,
}: UploadQueueGuardDialogProps) => {
  const { t } = useTranslation();
  const hasErrors = errorFilesCount > 0;
  const isUploading = uploadingFilesCount > 0;
  const isSuccess = !isUploading && !hasErrors && displayTotalFilesCount > 0;
  const labels = useMemo(
    () =>
      isSuccess
        ? {
            title: t('components.uploadQueueGuardDialog.successTitle'),
            confirmLabel: t('components.uploadQueueGuardDialog.successConfirm'),
            cancelLabel: t('close'),
          }
        : {
            title: t('components.uploadQueueGuardDialog.title'),
            confirmLabel: t('components.uploadQueueGuardDialog.confirm'),
            cancelLabel: t('components.uploadQueueGuardDialog.openFileManager'),
          },
    [isSuccess, t]
  );

  return (
    <ConfirmationDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={labels.title}
      CancelButtonProps={
        isSuccess
          ? {
              label: labels.cancelLabel,
            }
          : {
              label: labels.cancelLabel,
              onClick: onOpenFileManager,
            }
      }
      ConfirmButtonProps={{
        label: labels.confirmLabel,
      }}
    >
      <div className="text-sm">
        {isSuccess && (
          <p className="leading-5">
            {t('components.uploadQueueGuardDialog.successDescription')}
          </p>
        )}
        {isUploading && (
          <p className="leading-5 whitespace-pre-wrap">
            {t('components.uploadQueueGuardDialog.description', {
              count: displayTotalFilesCount,
            })}
          </p>
        )}
        {hasErrors && (
          <p className="mt-2 leading-5">
            {t('components.uploadQueueGuardDialog.errorLine', {
              count: errorFilesCount,
            })}
          </p>
        )}
      </div>
    </ConfirmationDialog>
  );
};
